/**
 * Defect List component.
 *
 * Mirrors the Passenger List pattern: input + Add button, listed items
 * with Remove. Uses a textarea for multi-line defect descriptions.
 *
 * @module defect-list
 */

import { MAX_DEFECTS } from '../utils/constants.js';

// ---------------------------------------------------------------------------
// Pure helper functions (exported for testing)
// ---------------------------------------------------------------------------

/**
 * Attempts to add a defect description to the list.
 * @param {string[]} defects - Current defect descriptions
 * @param {string} description - Defect description to add
 * @returns {{ defects: string[], error: string|null }}
 */
export function addDefect(defects, description) {
  const trimmed = (typeof description === 'string') ? description.trim() : '';
  if (trimmed === '') {
    return { defects, error: 'Defect description must not be empty' };
  }
  if (defects.length >= MAX_DEFECTS) {
    return { defects, error: `Maximum of ${MAX_DEFECTS} defects reached` };
  }
  return { defects: [...defects, trimmed], error: null };
}

/**
 * Removes a defect at the given index.
 * @param {string[]} defects - Current defect descriptions
 * @param {number} index - Index to remove
 * @returns {string[]} New defect list
 */
export function removeDefect(defects, index) {
  if (index < 0 || index >= defects.length) return [...defects];
  return defects.filter((_, i) => i !== index);
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * Creates a Defect List component.
 *
 * @param {Object} [config={}]
 * @param {string[]} [config.initialDefects=[]] - Pre-populated defect descriptions
 * @param {function} [config.onChange] - Callback when list changes
 * @returns {HTMLElement}
 */
export function createDefectList({ initialDefects, onChange } = {}) {
  let defects = Array.isArray(initialDefects) ? [...initialDefects] : [];

  const container = document.createElement('div');
  container.className = 'defect-list';

  // Title
  const title = document.createElement('h4');
  title.className = 'defect-list__title';
  title.textContent = 'Defects';
  container.appendChild(title);

  // List container
  const listEl = document.createElement('ul');
  listEl.className = 'defect-list__items';
  container.appendChild(listEl);

  // Message area
  const messageEl = document.createElement('div');
  messageEl.className = 'defect-list__message';
  messageEl.setAttribute('role', 'status');
  container.appendChild(messageEl);

  // Input row
  const inputRow = document.createElement('div');
  inputRow.className = 'defect-list__input-row';

  const input = document.createElement('textarea');
  input.className = 'defect-list__input';
  input.placeholder = 'Describe the defect...';
  input.rows = 3;
  input.setAttribute('aria-label', 'Defect description');
  inputRow.appendChild(input);

  const addBtn = document.createElement('button');
  addBtn.type = 'button';
  addBtn.className = 'defect-list__add-btn';
  addBtn.textContent = 'Add';
  inputRow.appendChild(addBtn);

  container.appendChild(inputRow);

  // Counter
  const counterEl = document.createElement('span');
  counterEl.className = 'defect-list__counter';
  container.appendChild(counterEl);

  // --- Rendering ---

  function render() {
    listEl.innerHTML = '';
    for (let i = 0; i < defects.length; i++) {
      const li = document.createElement('li');
      li.className = 'defect-list__item';

      const descSpan = document.createElement('span');
      descSpan.className = 'defect-list__description';
      descSpan.textContent = defects[i];
      li.appendChild(descSpan);

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'defect-list__remove-btn';
      removeBtn.textContent = 'Remove';
      removeBtn.setAttribute('aria-label', `Remove defect ${i + 1}`);
      removeBtn.addEventListener('click', () => {
        defects = removeDefect(defects, i);
        render();
        notifyChange();
      });
      li.appendChild(removeBtn);

      listEl.appendChild(li);
    }

    counterEl.textContent = `${defects.length} / ${MAX_DEFECTS}`;
    addBtn.disabled = defects.length >= MAX_DEFECTS;
    input.disabled = defects.length >= MAX_DEFECTS;
  }

  function notifyChange() {
    messageEl.textContent = '';
    container.dispatchEvent(new CustomEvent('defects-change', {
      bubbles: true,
      detail: { defects: [...defects] },
    }));
    if (typeof onChange === 'function') {
      onChange([...defects]);
    }
  }

  // --- Event handlers ---

  addBtn.addEventListener('click', () => {
    const result = addDefect(defects, input.value);
    if (result.error) {
      messageEl.textContent = result.error;
    } else {
      defects = result.defects;
      input.value = '';
      messageEl.textContent = '';
      render();
      notifyChange();
    }
  });

  // Ctrl+Enter / Cmd+Enter to add (Enter alone inserts newline in textarea)
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      addBtn.click();
    }
  });

  // Initial render
  render();

  /** Get the current defect descriptions. */
  container.getDefects = () => [...defects];

  return container;
}
